import { createContext, createElement, useContext, useMemo, useState, type ReactNode } from "react";
import { usePersonen } from "../api/abfragen";
import type { Aufgabe, Frist } from "../api/typen";

/** W82 Web-5f (D-562, H2): die Personen-Ansicht der Startseite, geteilt zwischen dem Menü am Kürzel und der Startseite.
 *  `"alle"` oder die Kennung einer Person; nicht gespeichert, gilt bis zum Neuladen wie das frühere Segment. */
export type PersonAnsicht = { person: string; setPerson(p: string): void };

/** Ohne Anbieter (Unit-Tests einer Seite ohne Schale) gilt „alle“ und die Wahl wirkt nicht. */
export const PersonAnsichtKontext = createContext<PersonAnsicht>({ person: "alle", setPerson: () => {} });

/** Anbieter in der Schale um den Inhalt. Fällt die gewählte Person weg (gelöscht) oder bleibt nur eine, gilt „alle“. */
export function PersonAnsichtWurzel(props: { children: ReactNode }) {
  const [gewaehlt, setGewaehlt] = useState("alle");
  const { data: personen } = usePersonen();
  // mit höchstens einer Person gibt es nichts zu wählen (das Fenster zeigt nur „Alle“, H1): auch dann gilt „alle“
  const weg = personen !== undefined && (personen.length <= 1 || !personen.some((p) => p.id === gewaehlt));
  const person = gewaehlt !== "alle" && weg ? "alle" : gewaehlt;
  const wert = useMemo(() => ({ person, setPerson: setGewaehlt }), [person]);
  return createElement(PersonAnsichtKontext.Provider, { value: wert }, props.children);
}

export function usePersonAnsicht(): PersonAnsicht {
  return useContext(PersonAnsichtKontext);
}

/** Vorname der gewählten Person (wie die Zeile „Ansicht: Ben“), null bei „alle“ oder solange die Personen fehlen. */
export function useGewaehltName(): string | null {
  const { person } = usePersonAnsicht();
  const { data: personen } = usePersonen();
  const gewaehlt = person === "alle" ? null : (personen?.find((p) => p.id === person) ?? null);
  return gewaehlt ? (gewaehlt.name.split(" ")[0] ?? gewaehlt.name) : null;
}


/** Regel R-P2 (W82 Web-5h1, BS09, gilt für Web, iOS-5 und Android): die Person einer Frist kommt aus dem Feld des Servers
 *  (`FristAus.person_id`, `FristAus.gemeinsam`, Server 5h-S und 5h-S2), ohne Karte der Briefe. Person P gewählt: eine Frist zählt,
 *  wenn sie gemeinsam ist oder `person_id == P`; eine Frist ohne Brief mit `person_id` null zählt auch (sie gehört dem Haushalt;
 *  5h-S meldet dort `gemeinsam: false`, 5h-S2 `true`). Eine Frist mit Brief ohne die Felder (älterer Server) zählt nur bei
 *  „alle“: die Ansicht zeigt nie Fremdes. Eine Stelle für Fristen-Seite, Startseite und Aufgaben. */
export function fristZurPerson(frist: Frist, person: string): boolean {
  if (person === "alle") return true;
  if (frist.gemeinsam === true || (frist.person_id != null && frist.person_id === person)) return true;
  return !frist.dokument_id && frist.person_id == null;
}

/** Eine Aufgabe folgt ihrer Frist (R-P2), ohne Frist (oder mit einer nicht mehr geladenen) dem Haushalt (Bestand 5f). */
export function aufgabeGehoertZu(aufgabe: Aufgabe, person: string, fristen: readonly Frist[]): boolean {
  if (person === "alle" || !aufgabe.frist_id) return true;
  const frist = fristen.find((f) => f.id === aufgabe.frist_id);
  return frist ? fristZurPerson(frist, person) : true;
}

/** Fristen und Aufgaben der gewählten Person (R-P2) ohne Einzelabfragen der Briefe: `bereit` ist wahr, sobald die Fristen da
 *  sind (`geladen`). Gebraucht werden die offenen Fristen und alle für die Säulen (`saeulen`). */
export function usePersonFristen(
  person: string,
  daten: { offen: Frist[]; alle: Frist[] | undefined; aufgaben: Aufgabe[]; geladen: boolean },
): { offen: Frist[]; alle: Frist[] | undefined; aufgaben: Aufgabe[]; bereit: boolean } {
  const gewaehlt = person !== "alle";
  return useMemo(() => {
    if (!gewaehlt) return { offen: daten.offen, alle: daten.alle, aufgaben: daten.aufgaben, bereit: true };
    const alleFristen = daten.alle ?? daten.offen;
    return {
      offen: daten.offen.filter((f) => fristZurPerson(f, person)),
      alle: daten.alle?.filter((f) => fristZurPerson(f, person)),
      aufgaben: daten.aufgaben.filter((a) => aufgabeGehoertZu(a, person, alleFristen)),
      bereit: daten.geladen,
    };
  }, [gewaehlt, person, daten.offen, daten.alle, daten.aufgaben, daten.geladen]);
}
